<template>
  <div :style="width ? 'width: ' + width + 'px' : ''" data-testid="regimen_report_card">
    <v-card>
      <v-toolbar v-if="show_toolbar" flat>
        <v-toolbar-title>
          <template v-if="instance_title">{{ instance_title }}</template>
          <template v-else>{{ exercise_title }}</template>
        </v-toolbar-title>
        <v-spacer />
        <v-icon
          v-if="hover_report.exercise_type == 'interactive'"
          left
          small
          grey
          >
            category
          </v-icon>
        <v-icon v-else small grey>ondemand_video</v-icon>
      </v-toolbar>
      <v-divider v-if="show_toolbar" />

      <v-card-text v-if="reports.length == 0">{{ $t('regimen_report_card.nothing_to_show') }}</v-card-text>
      <v-card-text v-else style="padding: 0px">

        <!-- Graph arranged on top of info for small screens -->
        <div v-if="display_graph && phone_screen" style="overflow: hidden">
          <div
            :id="plot_div_id"
            class="regimen_report_card_plotly"
            :style="plotly_style"
          >
          </div>
        </div>
        <v-row class="mb-6" no-gutters>

          <!-- Inline graph arranged next to info -->
          <v-col v-if="display_graph && !phone_screen" cols="sm">
            <v-select
              v-if="show_graph_field_selector"
              v-model="selected_graph_fields"
              :items="graph_dataset_options"
              item-text="text"
              item-value="value"
              :label="$t('regimen_report_card.fields')"
              multiple
              outlined
              dense
              hide-details
              data-testid="regimen_report_card_graph_field_selector"
              class="regimen_report_card_graph_field_selector"
              @change="on_graph_field_selection_change"
            >
              <template v-slot:item="data">
                <v-list-item-icon>
                  <v-icon v-if="selected_graph_fields.includes(data.item.value)">
                    mdi-check-box-outline
                  </v-icon>
                  <v-icon v-else>
                    mdi-checkbox-blank-outline
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title v-html="data.item.text" />
                  <v-list-item-subtitle>
                    {{ data.item.axis_title }}<span v-if="data.item.axis_suffix"> ({{ data.item.axis_suffix }})</span>
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-if="data.item.disabled_reason" class="error--text">
                    {{ data.item.disabled_reason }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:selection="{ index }">
                <span v-if="index === 0">
                  {{ $t('regimen_report_card.fields_selected', { num: selected_graph_fields.length }) }}
                </span>
              </template>
            </v-select>
            <div style="overflow: hidden">
              <div
                :id="plot_div_id"
                class="regimen_report_card_plotly"
                :style="plotly_style"
              >
              </div>
            </div>
          </v-col>
          <v-col cols="sm">
            <div>
              <div
                v-if="hover_report"
                class="hover_report_table_wrapper"
                style="height: 450px; position: relative"
              >
                <div
                  class="hover_report_table_inner_wrapper"
                >
                  <table class="hover_report_table">
                    <tr
                      v-if="
                        (hover_report.regimen_instance_title
                          ? hover_report.regimen_instance_title
                          : exercise_title) != exercise_title
                      "
                    >
                      <td
                        class="hover_report_label hover_report_label_overview"
                      >
                        {{ $t("common.title") }}
                      </td>
                      <td
                        class="hover_report_value hover_report_value_overview"
                      >
                        {{
                          hover_report.regimen_instance_title
                            ? hover_report.regimen_instance_title
                            : exercise_title
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td
                        class="hover_report_label hover_report_label_overview"
                      >
                        {{ $t("common.exercise") }}
                      </td>
                      <td
                        class="hover_report_value hover_report_value_overview"
                      >
                        {{ exercise_title }}
                      </td>
                    </tr>
                    <tr>
                      <td
                        class="hover_report_label hover_report_label_overview"
                      >
                        {{ $t("common.date") }}
                      </td>
                      <td
                        class="hover_report_value hover_report_value_overview"
                      >
                        {{ hover_report_date_date }}
                        {{ hover_report_date_time }}
                      </td>
                    </tr>
                    <tr>
                      <td
                        class="hover_report_label hover_report_label_overview"
                      >
                        {{ $t("regimen_report_card.time_spent") }}
                      </td>
                      <td
                        class="hover_report_value hover_report_value_overview"
                      >
                        {{ hover_report_time }}
                      </td>
                    </tr>
                    <tr v-if="hover_report && hover_report.level">
                      <td
                        class="hover_report_label hover_report_label_overview"
                      >
                        {{ $t("common.level") }}
                      </td>
                      <td
                        class="hover_report_value hover_report_value_overview"
                      >
                        {{ hover_report.level }}
                      </td>
                    </tr>
                    <tr v-if="hover_report && hover_report.level">
                      <td
                        class="hover_report_label hover_report_label_overview"
                      >
                        {{ $t("regimen_report_card.level_completed") }}
                      </td>
                      <td
                        class="hover_report_value hover_report_value_overview"
                      >
                        <span v-if="hover_report.level_completed">{{ $t("common.yes") }}</span>
                        <span v-else>{{ $t("common.no") }}</span>
                      </td>
                    </tr>
                  </table>

                  <table class="hover_report_table" style="margin-top: 20px">
                    <!-- Score is displayed via Vue Template -->
                    <tr v-if="OpticalGymScores[hover_report.exercise_id]">
                      <td colspan="2">
                        <component
                          :is="hover_report.exercise_id + '-score'"
                          context="regimen_report_card"
                          :score="hover_report.score"
                          :exercise="exercises[hover_report.exercise_id]"
                          :config="hover_report.params"
                        />
                      </td>
                    </tr>

                    <tr
                      v-for="(field_config, field) in exercise.reporting"
                      v-else
                      :key="field"
                    >
                      <td
                        v-if="
                          display_field_title(
                            hover_report.score[field],
                            field_config
                          )
                        "
                        :class="
                          'hover_report_label hover_report_label_score' +
                          (display_field_value(
                            hover_report.score[field],
                            field_config
                          )
                            ? ''
                            : ' hover_report_label_empty_value')
                        "
                      >
                        {{ render_field_title(field_config, field) }}
                      </td>
                      <td
                        v-if="
                          display_field_value(
                            hover_report.score[field],
                            field_config
                          )
                        "
                        class="hover_report_value hover_report_value_score"
                      >
                        {{ format_value(field) }}
                      </td>
                    </tr>

                    <!-- Questionnaire -->
                    <template
                      v-if="hover_report.exercise_type == 'questionnaire'"
                    >
                      <tr
                        v-for="(
                          item, item_idx
                        ) in hover_report_questionnaire_answers"
                        :key="item_idx"
                      >
                        <td class="hover_report_label hover_report_label_score">
                          {{ item.item.title }}
                        </td>
                        <td class="hover_report_value hover_report_value_score">
                          {{ item.answer_display }}
                        </td>
                      </tr>
                    </template>

                    <!-- Single Question -->
                    <template v-if="hover_report.exercise_type == 'question'">
                      <tr v-if="hover_report_question_answer">
                        <td class="hover_report_label hover_report_label_score">
                          {{ $t("regimen_report_card.answer") }}
                        </td>
                        <td class="hover_report_value hover_report_value_score">
                          {{ hover_report_question_answer.answer_display }}
                        </td>
                      </tr>
                    </template>

                    <!-- Feedback -->
                    <tr v-if="hover_report.feedback">
                      <td
                        colspan="2"
                        style="
                          width: 400px;
                          font-weight: bold;
                          padding-top: 20px;
                          text-align: center;
                        "
                      >
                        {{ $t("regimen_report_card.feedback") }}
                      </td>
                    </tr>
                    <tr v-if="hover_report.feedback">
                      <td colspan="2">{{ hover_report.feedback }}</td>
                    </tr>
                  </table>
                  <div
                    v-if="
                      !hover_report.exercise_type ||
                      hover_report.exercise_type == 'interactive'
                    "
                    class="regimen_report_card_buttons"
                  >
                    <v-btn small @click="options_dialog = true" data-testid="regimen_report_card_view_config" class="mr-6 mt-6">
                      {{ $t("regimen_report_card.view_config") }}
                    </v-btn>
                    <v-btn small @click="events_dialog = true" data-testid="regimen_report_card_view_events" class="mr-6 mt-6">
                      {{ $t("regimen_report_card.view_events") }}
                    </v-btn>
                    <v-btn v-if="current_user && current_user.is_admin && hover_report && hover_report.id" small :href="'/api/report/' + hover_report.id" target="_blank" data-testid="regimen_report_card_view_report" class="mr-6 mt-6">
                      <v-icon small left>mdi-shield</v-icon>
                      {{ $t("regimen_report_card.view_report") }}
                    </v-btn>
                    <v-btn v-if="current_user && current_user.is_admin" small @click="raw_score_dialog = true" data-testid="regimen_report_card_raw_score" class="mr-6 mt-6">
                      <v-icon small left>mdi-shield</v-icon>
                      {{ $t("regimen_report_card.raw_score") }}
                    </v-btn>
                    <div
                      v-if="
                        allow_edit_assigned &&
                        regimen &&
                        hover_report.regimen_instance_id &&
                        hover_report_exercise_instance
                      "
                    >
                      <v-btn
                        small
                        @click="
                          regimen_edit_dialogs[
                            hover_report.regimen_instance_id
                          ] = true
                        "
                        class="mt-6"
                        data-testid="regimen_report_card_edit_exercise"
                      >
                        {{ $t("regimen_report_card.edit_exercise") }}
                      </v-btn>

                      <v-dialog
                        v-model="
                          regimen_edit_dialogs[
                            hover_report.regimen_instance_id
                          ]
                        "
                        scrollable
                        max-width="1200"
                      >
                        <v-card
                          v-if="
                            regimen_edit_dialogs[
                              hover_report.regimen_instance_id
                            ]
                          "
                        >
                          <v-card-title style="padding: 0px">
                            <v-toolbar>
                              <v-btn
                                icon
                                @click="
                                  regimen_edit_dialogs[
                                    hover_report.regimen_instance_id
                                  ] = false
                                "
                              >
                                <v-icon>close</v-icon>
                              </v-btn>
                              <v-toolbar-title>
                                <span>{{ exercise_title }}</span>
                              </v-toolbar-title>
                            </v-toolbar>
                          </v-card-title>
                          <v-card-text class="pt-6">
                            <RegimenExerciseEdit
                              v-model="hover_report_exercise_instance"
                              :allow_select_exercise="false"
                              @save="
                                save_exercise_instance_edit(
                                  hover_report_exercise_instance
                                )
                              "
                            />
                          </v-card-text>
                        </v-card>
                      </v-dialog>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-dialog v-model="options_dialog" max-width="600px" :fullscreen="phone_screen">
      <v-card v-if="options_dialog">
        <v-card-title>
          <v-toolbar grey flat>
            <v-btn icon @click="options_dialog = false" data-testid="regimen_report_card_options_dialog_false">
              <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>{{ $t("regimen_report_card.exercise_configuration") }}</v-toolbar-title>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <table class="hover_report_options">
            <tr>
              <td
                colspan="2"
                style="
                  width: 400px;
                  font-weight: bold;
                  padding-top: 20px;
                  text-align: center;
                "
              >
                {{ $t("regimen_report_card.configuration") }}
              </td>
            </tr>
            <tr v-for="(value, field) in hover_report.params" :key="field">
              <td
                v-if="field != 'level'"
                class="hover_report_label"
                style="min-width: 100px"
              >
                {{ field }}
              </td>
              <td v-if="field != 'level'" class="hover_report_value">
                {{ value }}
              </td>
            </tr>
            <tr>
              <td
                colspan="2"
                style="
                  width: 400px;
                  font-weight: bold;
                  padding-top: 20px;
                  text-align: center;
                "
              >
                {{ $t("regimen_report_card.calibration") }}
              </td>
            </tr>
            <tr v-for="(value, field) in hover_report.calibration" :key="field">
              <template
                v-if="
                  current_user.is_admin ||
                  (field != 'fingerprint' &&
                    field != 'id' &&
                    field != 'user_id')
                "
              >
                <td class="hover_report_label" style="min-width: 100px">
                  {{ field }}
                </td>
                <td class="hover_report_value">{{ value }}</td>
              </template>
            </tr>
          </table>
          <v-layout justify-space-around class="mt-6">
            <v-flex>
              <v-btn small @click="launch_exercise_with_config(false)" data-testid="regimen_report_card_launch_exercise"><v-icon small left color="blue">mdi-play</v-icon>{{ $t('regimen_report_card.launch_exercise') }}</v-btn>
            </v-flex>
            <v-flex>
              <v-btn small @click="launch_exercise_with_config(true)" data-testid="regimen_report_card_launch_exercise_include_calibration"><v-icon small left color="blue">mdi-play</v-icon>{{ $t('regimen_report_card.launch_exercise_match_calibration') }}</v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="raw_score_dialog" max-width="800px" :fullscreen="phone_screen">
      <v-card v-if="raw_score_dialog">
        <v-card-title style="padding: 0px">
          <v-toolbar>
            <v-btn icon @click="raw_score_dialog = false" data-testid="regimen_report_card_raw_score_dialog_close">
              <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>{{ $t("regimen_report_card.raw_score") }}</v-toolbar-title>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <pre
            class="raw_score_json"
            style="overflow: auto; max-height: 70vh; padding: 16px; background: #f5f5f5; border-radius: 4px; user-select: all"
          >{{ raw_score_json }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="events_dialog" max-width="1200px" :fullscreen="phone_screen">
      <v-card v-if="events_dialog">
        <v-card-title style="padding: 0px">
          <v-toolbar>
            <v-btn icon @click="events_dialog = false" data-testid="regimen_report_card_events_dialog_close">
              <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>{{ $t("regimen_report_card.events") }}</v-toolbar-title>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <table class="hover_report_events">
            <tr>
              <th>{{ $t("regimen_report_card.time") }}</th>
              <th>{{ $t("regimen_report_card.type") }}</th>
              <th>{{ $t("regimen_report_card.details") }}</th>
            </tr>
            <tr v-for="(event, index) in hover_report_events" :key="index">
              <td>{{ format_minute_seconds(event.event_time) }}</td>
              <td>{{ event.event_type }}</td>
              <td>
                <span
                  v-for="(event_value, event_field) in event"
                  :key="event_field"
                  class="event_values"
                >
                  <span v-if="display_event_field(event_field)">
                    <span>{{ event_field }}:&nbsp;</span>
                    <span>{{ event_value }}</span>
                    <span class="event_values_comma">,&nbsp;</span>
                  </span>
                </span>
              </td>
            </tr>
          </table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import $ from "jquery";
import { Chance } from "chance";
let chance = Chance();
import { mapState } from "vuex";
import OpticalGymScores from "../../../exercises/OpticalGymScores.js";
import RegimenExerciseEdit from "./RegimenExerciseEdit.vue";
import axios from "axios";

// Map ConfigForms and Score components
let exercise_components = {};
for (let exercise_id of Object.keys(OpticalGymScores)) {
  exercise_components[exercise_id + "-score"] = OpticalGymScores[exercise_id];
}

import plotly_dark from "../plotly_dark.json";
import { exercise_score_display } from "../classes/exercise.ts";
import nunjucks from 'nunjucks';
import {
  projectRegimenExercises,
  replaceProjectedExercise,
  reportMatchesProjectedExercise,
} from "../lib/regimen_grid";

export default {
  components: Object.assign(exercise_components, { RegimenExerciseEdit }),
  props: {
    exercise: {
      type: Object,
      required: true,
    },
    reports: {
      type: Array,
      required: true,
    },
    display_graph: {
      type: Boolean,
      default: true,
    },
    plotly_style: {
      type: String,
      default: "height: 420px; width: 600px",
    },
    max_ticks: {
      type: Number,
      default: 30,
    },
    show_toolbar: {
      type: Boolean,
      default() {
        return true;
      },
    },
    regimen: {
      type: Object,
      default: () => null,
    },
    width: {
      type: Number,
      default: 0,
    },
    allow_edit_assigned: {
      type: Boolean,
      default: () => true,
    },
  },
  data() {
    var hover_report;
    if (this.reports.length) {
      hover_report = this.reports[this.reports.length - 1];
    } else {
      hover_report = null;
    }

    let plot_div_id =
      "regimen_report_card_" + chance.string({ length: 10, alpha: true });

    let loading = this.display_graph;

    let regimen_edit_dialogs = {};
    if (this.regimen) {
      for (let exercise of projectRegimenExercises(this.regimen.exercises)) {
        if (
          exercise.uuid &&
          !regimen_edit_dialogs.hasOwnProperty(exercise.uuid)
        ) {
          regimen_edit_dialogs[exercise.uuid] = false;
        }
      }
    }

    return {
      hover_report,
      options_dialog: false,
      events_dialog: false,
      raw_score_dialog: false,
      lazy: false,
      loading,
      plot_div_id,
      regimen_edit_dialogs,
      OpticalGymScores,
      selected_graph_fields: [],
    };
  },
  computed: {
    ...mapState(["exercises", "phone_screen"]),
    graph_dataset_base_options() {
      if (!this.exercise || !this.exercise.reporting) {
        return [];
      }
      let options = [];
      for (let field of Object.keys(this.exercise.reporting)) {
        let field_config = this.exercise.reporting[field];
        if (!field_config.graph) {
          continue;
        }
        let field_title =
          field_config.title[this.$i18n.lang()] || field_config.title["en"] || field;
        options.push({
          value: field,
          text: this.render_nunjucks_template_for_graph(field_title, field),
          axis_title: this.graph_axis_title_for_field(field, field_config),
          axis_suffix: this.graph_axis_suffix_for_field(field, field_config),
          graph_show_default: field_config.graph_show_default !== false,
        });
      }
      return options;
    },
    selected_graph_axis_titles() {
      let axis_titles = [];
      for (let field of this.selected_graph_fields) {
        let option = this.graph_dataset_base_options.find((item) => item.value == field);
        if (!option || axis_titles.includes(option.axis_title)) {
          continue;
        }
        axis_titles.push(option.axis_title);
      }
      return axis_titles;
    },
    graph_dataset_options() {
      let selected_axes = this.selected_graph_axis_titles;
      let axis_limit_reached = selected_axes.length >= 2;
      return this.graph_dataset_base_options.map((option) => {
        let already_selected = this.selected_graph_fields.includes(option.value);
        let disabled_reason = "";
        let disabled = false;
        if (
          axis_limit_reached &&
          !already_selected &&
          !selected_axes.includes(option.axis_title)
        ) {
          disabled = true;
          disabled_reason = this.$t("regimen_report_card.max_two_axes_selected");
        }
        return {
          ...option,
          disabled,
          disabled_reason,
        };
      });
    },
    show_graph_field_selector() {
      return this.graph_dataset_base_options.length > 1;
    },
    exercise_instance_by_uuid() {
      let instances = {};
      if (!this.regimen) {
        return instances;
      }
      for (let exercise of projectRegimenExercises(this.regimen.exercises)) {
        if (exercise.uuid) {
          instances[exercise.uuid] = exercise;
        }
      }

      return instances;
    },
    hover_report_exercise_instance: {
      get() {
        if (!this.regimen || !this.hover_report) return null;
        return projectRegimenExercises(this.regimen.exercises).find(
          (exercise) => reportMatchesProjectedExercise(this.hover_report, exercise),
        ) || null;
      },
      set(value) {
        if (value && this.regimen) {
          replaceProjectedExercise(this.regimen.exercises, value);
        }
      },
    },
    exercise_title() {
      // TODO: This should be possible since exercise should always exist
      if (!this.exercise) {
        return this.$t("regimen_report_card.unknown_exercise");
      }
      if (!this.exercise.title) {
        if (this.exercise.exercise_type == "questionnaire") {
          return this.$t("common.questionnaire");
        } else if (this.exercise.exercise_type == "question") {
          return this.$t("common.single_question");
        } else {
          return this.$t("regimen_report_card.unknown_exercise");
        }
      }
      if (typeof this.exercise.title == "string") {
        return this.exercise.title;
      }
      let locale = this.$i18n.lang();
      if (this.exercise.title[locale]) {
        return this.exercise.title[locale];
      } else {
        return this.exercise.title.en;
      }
    },
    instance_title() {
      if (!this.reports.length) {
        return "";
      }

      let title = this.reports[0].regimen_instance_title;
      for (let report of this.reports) {
        if (title != report.regimen_instance_title) {
          return "";
        }
      }

      return title;
    },
    hover_report_date_date() {
      let date = new Date(this.hover_report.start_time);
      var options = {
        month: "long",
        day: "numeric",
        year: "numeric",
      };
      let formatter = new Intl.DateTimeFormat(this.$i18n.lang(), options);
      return formatter.format(date);
    },
    hover_report_date_time() {
      let date = new Date(this.hover_report.start_time);
      var options = {
        hour: "numeric",
        minute: "numeric",
      };
      let formatter = new Intl.DateTimeFormat(this.$i18n.lang(), options);
      return formatter.format(date);
    },
    hover_report_time() {
      let time_played = this.hover_report.time_played / 1000;
      let time_paused = this.hover_report.time_paused / 1000;
      let time_total = this.hover_report.time_total / 1000;

      let output = this.format_minute_seconds(time_played);

      if (time_paused) {
        output +=
          " (" +
          this.format_minute_seconds(time_paused) +
          this.$t("common.paused") +
          ", " +
          this.format_minute_seconds(time_total) +
          " " +
          this.$t("common.total") +
          ")";
      }
      return output;
    },
    hover_report_events() {
      let events = [];

      for (var event_type in this.hover_report.events) {
        for (var i in this.hover_report.events[event_type]) {
          let event = this.hover_report.events[event_type][i];
          event.event_time =
            (event.timestamp - this.hover_report.start_time) / 1000;
          events.push(event);
        }
      }

      events.sort(function (a, b) {
        return a.timestamp - b.timestamp;
      });
      return events;
    },
    raw_score_json() {
      if (!this.hover_report || !this.hover_report.score) {
        return "{}";
      }
      return JSON.stringify(this.hover_report.score, null, 2);
    },
    hover_report_questionnaire_answers() {
      let answers = [];
      if (this.exercise.exercise_type != "questionnaire") {
        return [];
      }
      if (!this.hover_report.params.items) {
        return [];
      }

      for (let item of this.hover_report.params.items) {
        let answer = this.hover_report.score[item.id];

        if (answer === null || answer === undefined) continue;

        let answer_display = this.question_answer_display(item, answer);

        if (item) {
          answers.push({
            item,
            answer,
            answer_display,
          });
        }
      }

      return answers;
    },
    hover_report_question_answer() {
      if (this.exercise.exercise_type != "question") {
        return null;
      }

      let item = this.hover_report.params;

      let answer = this.hover_report.score[item.id];

      if (answer === null || answer === undefined) return;

      let answer_display = this.question_answer_display(item, answer);

      return {
        item,
        answer,
        answer_display,
      };
    },
    questionnaire_graphable_items() {
      let items = [];
      if (this.reports[0].exercise_type == "question") {
        for (let report of this.reports) {
          if (report.params) {
            let item = report.params;

            // Check if item already exists by looking at item.id and item.instance_id
            let exists = false;
            for (let existing_item of items) {
              if (
                existing_item.id == item.id &&
                existing_item.instance_id == report.instance_id
              ) {
                exists = true;
                break;
              }
            }
            if (exists) continue;

            if (
              item.type == "number-field" ||
              item.type == "mood" ||
              item.type == "rating"
            ) {
              item.instance_id = report.instance_id;
              items.push(item);
            }
          }
        }
      } else if (this.reports[0].exercise_type == "questionnaire") {
        for (let report of this.reports) {
          if (report.params && report.params.items) {
            for (let item of report.params.items) {
              // Check if item already exists by looking at item.id and item.instance_id
              let exists = false;
              for (let existing_item of items) {
                if (
                  existing_item.id == item.id &&
                  existing_item.instance_id == report.instance_id
                ) {
                  exists = true;
                  break;
                }
              }
              if (exists) continue;

              if (
                item.type == "number-field" ||
                item.type == "mood" ||
                item.type == "rating"
              ) {
                item.instance_id = report.instance_id;
                items.push(item);
              }
            }
          }
        }
      }

      return items;
    },
    ...mapState(["current_user"]),
  },
  watch: {
    reports() {
      this.plot();
      if (this.reports.length) {
        this.hover_report = this.reports[this.reports.length - 1];
      } else {
        this.hover_report = null;
      }
    },
    graph_dataset_base_options: {
      handler() {
        this.sync_selected_graph_fields();
      },
      deep: true,
    },
    selected_graph_fields() {
      this.plot();
    },
    regimen() {
      if (this.regimen) {
        for (let exercise of projectRegimenExercises(this.regimen.exercises)) {
          if (
            exercise.uuid &&
            !this.regimen_edit_dialogs.hasOwnProperty(exercise.uuid)
          ) {
            this.regimen_edit_dialogs[exercise.uuid] = false;
          }
        }
      }
    },
  },
  mounted() {
    this.sync_selected_graph_fields();
    if (this.display_graph) {
      this.load_plotly();
      this.wait_for_plotly();
    }
  },
  methods: {
    graph_axis_title_for_field(field, field_config = null) {
      let config = field_config || this.exercise.reporting[field];
      if (!config) {
        return field;
      }
      let field_title =
        config.title[this.$i18n.lang()] || config.title["en"] || field;
      field_title = this.render_nunjucks_template_for_graph(field_title, field);
      let axis_title = field_title;
      if (config.axis && config.axis[this.$i18n.lang()]) {
        axis_title = config.axis[this.$i18n.lang()];
        axis_title = this.render_nunjucks_template_for_graph(axis_title, field);
      } else if (config.axis && config.axis["en"]) {
        axis_title = config.axis["en"];
        axis_title = this.render_nunjucks_template_for_graph(axis_title, field);
      }
      return axis_title;
    },
    graph_axis_suffix_for_field(field, field_config = null) {
      let config = field_config || this.exercise.reporting[field];
      if (!config || !("suffix" in config)) {
        return "";
      }
      let suffix =
        config.suffix[this.$i18n.lang()] ||
        config.suffix["en"] ||
        "";
      if (!suffix) {
        return "";
      }
      return this.render_nunjucks_template_for_graph(suffix, field).trim();
    },
    apply_max_two_axis_limit(field_ids) {
      let selected_axes = [];
      let valid_fields = [];
      for (let field_id of field_ids) {
        let option = this.graph_dataset_base_options.find(
          (item) => item.value == field_id
        );
        if (!option) {
          continue;
        }
        if (
          !selected_axes.includes(option.axis_title) &&
          selected_axes.length >= 2
        ) {
          continue;
        }
        if (!selected_axes.includes(option.axis_title)) {
          selected_axes.push(option.axis_title);
        }
        valid_fields.push(field_id);
      }
      return valid_fields;
    },
    arrays_equal(arr1, arr2) {
      if (arr1.length != arr2.length) {
        return false;
      }
      for (let idx in arr1) {
        if (arr1[idx] != arr2[idx]) {
          return false;
        }
      }
      return true;
    },
    sync_selected_graph_fields() {
      let all_field_ids = this.graph_dataset_base_options.map((option) => option.value);
      if (!all_field_ids.length) {
        this.selected_graph_fields = [];
        return;
      }

      let current_selection = this.selected_graph_fields.filter((field) =>
        all_field_ids.includes(field)
      );

      if (!current_selection.length) {
        current_selection = this.graph_dataset_base_options
          .filter((option) => option.graph_show_default)
          .map((option) => option.value);
      }

      current_selection = this.apply_max_two_axis_limit(current_selection);

      if (!current_selection.length) {
        current_selection = [all_field_ids[0]];
      }

      if (!this.arrays_equal(this.selected_graph_fields, current_selection)) {
        this.selected_graph_fields = current_selection;
      }
    },
    on_graph_field_selection_change() {
      let selection = this.apply_max_two_axis_limit(this.selected_graph_fields);
      if (!selection.length) {
        let fallback = this.graph_dataset_base_options[0];
        if (fallback) {
          selection = [fallback.value];
        }
      }
      if (!this.arrays_equal(this.selected_graph_fields, selection)) {
        this.selected_graph_fields = selection;
      }
    },
    question_answer_display(item, answer) {
      let answer_display = answer;
      if (
        (item.type == "select" || item.type == "radio-group") &&
        item.items &&
        item.items.length
      ) {
        for (let option of item.items) {
          if (option.value == answer) {
            answer_display = option.text;
            break;
          }
        }
      }
      if (item.type == "checkboxes" && item.items && item.items.length) {
        answer_display = [];
        for (let option of item.items) {
          if (answer.includes(option.value)) {
            answer_display.push(option.text);
          }
        }
        answer_display = answer_display.join(", ");
      }
      if (item.type == "checkbox" || item.type == "switch") {
        answer_display = answer ? this.$t("common.yes") : this.$t("common.no");
      }
      if (item.type == "mood") {
        if (answer) {
          answer_display = this.mood_map(answer) + " (" + answer + ")";
        }
      }
      return answer_display;
    },
    load() {
      if (this.display_graph) {
        this.load_plotly();
        this.wait_for_plotly();
      }
    },
    mood_map(mood) {
      let mood_map = ["😟", "😕", "😐", "🙂", "😀"];
      if (mood) {
        if (mood_map[mood - 1]) {
          return mood_map[mood - 1];
        } else {
          return mood;
        }
      }
    },
    save_exercise_instance_edit(instance) {
      // Load the regimen fresh and replace the instance
      axios.get("/api/regimen/" + this.regimen.id).then((response) => {
        let resp = response.data;
        if (resp.status == "error") {
          messageBus.$emit("api-error", resp);
        } else {
          if (resp.data) {
            let regimen = resp.data;
            let instance_replace = replaceProjectedExercise(regimen.exercises, instance);
            if (!instance_replace) {
              messageBus.$emit("error", this.$t("regimen_report_card.unable_to_update_instance"));
            } else {
              axios
                .put("/api/regimen/" + regimen.id, regimen)
                .then((response) => {
                  let resp = response.data;
                  if (resp.status == "error") {
                    messageBus.$emit("api-error", resp);
                  } else {
                    this.$emit("regimen-updated", regimen);
                    this.regimen_edit_dialogs[instance.uuid] = false;
                    messageBus.$emit(
                      "success",
                      this.$t("regimen_report_card.assigned_exercise_updated")
                    );
                  }
                });
            }
          }
        }
      });
    },
    launch_exercise_with_config(include_calibration = false) {
      let params = this.hover_report.params;
      let calibration = this.hover_report.calibration;

      let url = "/api/exercise/" + this.hover_report.exercise_id + "/launch";
      if (this.hover_report.level) {
        url += "/" + this.hover_report.level;
      }

      const params_string = new URLSearchParams(params).toString();
      url += "?" + params_string;

      if (include_calibration) {
        for (var ckey in calibration) {
          if (typeof calibration[ckey] !== "object") {
            url +=
              "&calibration_override_" + ckey + "=" + calibration[ckey];
          }
        }
      }

      window.open(url);
    },
    display_event_field(field_name) {
      return !(
        field_name == "timestamp" ||
        field_name == "event_type" ||
        field_name == "event_time"
      );
    },
    format_minute_seconds(time_in_seconds) {
      let str_pad_left = function (string, pad, length) {
        return (new Array(length + 1).join(pad) + string).slice(-length);
      };

      time_in_seconds = Math.floor(time_in_seconds);

      var minutes = Math.floor(time_in_seconds / 60);
      var seconds = time_in_seconds - minutes * 60;
      return minutes + ":" + str_pad_left(seconds, "0", 2);
    },
    display_field_value(value, field_config) {
      if (value === null || value === undefined) {
        return false;
      }
      if (field_config.display == "boolean_label_only") {
        return false;
      }
      return true;
    },
    display_field_title(value, field_config) {
      if (field_config.display == "boolean_label_only") {
        return value;
      }
      if (value === null || value === undefined) {
        return false;
      }
      return true;
    },
    render_nunjucks_template(template_string, field) {
      if (!template_string) {
        return '';
      }

      let template_env = new nunjucks.Environment();

      // Global translation function
      template_env.addGlobal('t', (prefix, key) => {
        const full_key = key ? `${prefix}.${key}` : prefix;
        return this.$t(full_key);
      });

      const context = {
        exercise: this.exercise,
        field_id: field,
        score: this.hover_report ? this.hover_report.score : {},
        config: this.hover_report ? (this.hover_report.params || {}) : {},
      };

      try {
        return template_env.renderString(template_string, context);
      } catch (error) {
        console.error('Error rendering nunjucks template:', error);
        return template_string;
      }
    },
    render_nunjucks_template_for_graph(template_string, field) {
      if (!template_string) {
        return '';
      }

      let template_env = new nunjucks.Environment();

      // Global translation function
      template_env.addGlobal('t', (prefix, key) => {
        const full_key = key ? `${prefix}.${key}` : prefix;
        return this.$t(full_key);
      });

      const context = {
        exercise: this.exercise,
        field_id: field,
        score: {},
        config: {},
      };

      try {
        return template_env.renderString(template_string, context);
      } catch (error) {
        console.error('Error rendering nunjucks template:', error);
        return template_string;
      }
    },
    render_field_title(field_config, field) {
      let current_lang = this.$i18n.lang();
      let title = field_config.title[current_lang] || field_config.title["en"];
      return this.render_nunjucks_template(title, field);
    },
    format_value(field) {
      // Use exercise_score_display for consistent formatting and nunjucks template support
      return exercise_score_display(
        this.exercise,
        field,
        this.hover_report.score,
        this.hover_report.params,
        this.$i18n,
        true // include_suffix
      );
    },
    plot() {
      if (this.reports.length == 0) {
        return;
      }
      if (!window.Plotly) {
        return;
      }

      let self = this;

      let data, layout;
      if (
        this.reports[0].exercise_type == "interactive" ||
        !this.reports[0].exercise_type
      ) {
        ({ data, layout } = this.data_layout_interactive());
      } else if (
        this.reports[0].exercise_type == "questionnaire" ||
        this.reports[0].exercise_type == "question"
      ) {
        ({ data, layout } = this.data_layout_questionnaire());
      } else {
        ({ data, layout } = this.data_layout_video());
      }

      if (this.$vuetify.theme.dark) {
        layout.template = plotly_dark;
      }

      layout.modebar = {
        position: "left",
      };

      let options = {
        displayModeBar: true,
        modeBarButtons: [
          [],
          ["resetViews", "toImage", "zoomOut2d", "zoomIn2d"],
        ],
      };

      // plot element might not exist if we open RegimenReportCard then immediately close it
      var plot_element = document.getElementById(this.plot_div_id);
      if (plot_element) {
        window.Plotly.newPlot(this.plot_div_id, data, layout, options);

        plot_element.on("plotly_hover", function (data) {
          let i = data.points[0].x;
          let report = self.reports[i];
          $(".hover_report_table_wrapper").fadeOut(100, "swing", function () {
            self.hover_report = report;
            $(".hover_report_table_wrapper").fadeIn(100, "swing");
          });
        });
      } else {
        // TODO Check if this component is mounted and raise an error if it is
      }
    },
    data_layout_questionnaire() {
      let data = [];
      var layout = {
        showlegend: true,
        legend: {
          orientation: "h",
          x: 0.5,
          xanchor: "center",
          y: -0.2,
          yanchor: "top",
        },
        xaxis: {
          tickvals: [],
          ticktext: [],
        },
      };

      var trace = {
        x: [],
        y: [],
        text: [],
        type: "scatter",
        name: "",
        connectgaps: false,
      };

      // Don't show any hover label for this trace
      var null_trace = {
        x: [],
        y: [],
        text: [],
        mode: "none",
        type: "scatter",
        hoverinfo: "text",
      };

      var trace2 = {
        x: [],
        y: [],
        text: [],
        type: "scatter",
        name: "",
        yaxis: "y2",
        connectgaps: false,
      };

      // Hover template
      trace.hovertemplate = "%{text}";

      let items = this.questionnaire_graphable_items;
      let num_items = items.length;

      if (num_items == 0) {
        layout.yaxis = {
          title: this.$t("regimen_report_card.time_taken_seconds"),
          fixedrange: true,
        };
      }
      if (num_items >= 1) {
        layout.yaxis = {
          title: items[0].title,
        };
      }
      if (num_items >= 2) {
        layout.yaxis2 = {
          title: items[1].title,
          overlaying: "y",
          side: "right",
        };
      }

      // Calculate tick ratio;
      let tick_ratio = Math.round(this.reports.length / this.max_ticks);
      if (!tick_ratio) {
        tick_ratio = 1;
      }

      for (var i in this.reports) {
        let report = this.reports[i];
        let score = this.reports[i].score;

        if (!num_items) {
          let time_taken_ms = score["time_taken_ms"];
          let value = null;
          let text = "";
          if (time_taken_ms !== null && typeof time_taken_ms !== "undefined") {
            value = Math.round(time_taken_ms / 1000);
            let mins = Math.floor(time_taken_ms / 1000 / 60);
            let secs = value % 60;
            text = mins + ":" + this.zeroPad(secs, 2);
          }
          trace.text.push(text);
          trace.x.push(i);
          trace.y.push(value);
        }
        if (num_items >= 1) {
          let value = score[items[0].id];
          trace.x.push(i);
          trace.y.push(value);

          if (items[0].type == "mood" && value) {
            trace.text.push(this.mood_map(value));
            layout.yaxis.range = [1, 5];
          } else {
            trace.text.push(value);
          }
        }
        if (num_items >= 2) {
          let value = score[items[1].id];
          trace2.x.push(i);
          trace2.y.push(value);

          if (items[1].type == "mood") {
            trace2.text.push(this.mood_map(value));
            layout.yaxis2.range = [1, 5];
          } else {
            trace2.text.push(value);
          }
        }

        // X-Axis
        let date = new Date(report.start_time);
        var options = { month: "short", day: "numeric" };
        let formatter = new Intl.DateTimeFormat(this.$i18n.lang(), options);
        let ticktext = formatter.format(date);
        if (report.feedback) {
          ticktext += " 💬";
        }
        if (i % tick_ratio == 0 || report.feedback) {
          layout.xaxis.tickvals.push(i);
          layout.xaxis.ticktext.push(ticktext);
        }

        null_trace.x.push(i);
        null_trace.y.push(0);
        null_trace.text.push(ticktext);
      }
      data.push(trace);
      data.push(null_trace);

      if (num_items >= 2) {
        data.push(trace2);
      }

      return { data, layout };
    },

    data_layout_interactive() {
      let data = [];
      var layout = {
        showlegend: true,
        legend: {
          orientation: "h",
          x: 0.5,
          xanchor: "center",
          y: -0.2,
          yanchor: "top",
        },
        xaxis: {
          tickvals: [],
          ticktext: [],
        },
      };

      // Map axis_title -> { yaxisKey: 'y'|'y2', colours: [] } for shared axes
      let axis_by_title = {};
      let next_axis_slot = 1;
      let colorway_index = 0;
      let light_colors = ["blue", "orange", "#509e3d"];
      for (let field of this.selected_graph_fields) {
        let field_config = this.exercise.reporting[field];
        if (!field_config || !field_config.graph) {
          continue;
        }

        let field_title =
          field_config.title[this.$i18n.lang()] || field_config.title["en"];
        // Render field title through nunjucks template
        field_title = this.render_nunjucks_template_for_graph(field_title, field);

        let axis_title = field_title;
        if (field_config.axis && field_config.axis[this.$i18n.lang()]) {
          axis_title = field_config.axis[this.$i18n.lang()];
          axis_title = this.render_nunjucks_template_for_graph(axis_title, field);
        } else if (field_config.axis && field_config.axis["en"]) {
          axis_title = field_config.axis["en"];
          axis_title = this.render_nunjucks_template_for_graph(axis_title, field);
        }

        var trace = {
          x: [],
          y: [],
          type: "scatter",
          name: field_title,
          connectgaps: false,
        };

        // Hover template
        trace.hovertemplate = "%{y}";

        // Suffix - template with score: {} (no single report in graph), then trim
        let suffix = "";
        if ("suffix" in field_config) {
          suffix =
            field_config.suffix[this.$i18n.lang()] ||
            field_config.suffix["en"] ||
            "";
          if (suffix) {
            suffix = this.render_nunjucks_template_for_graph(suffix, field).trim();
          }
        }

        let colour = light_colors[colorway_index];
        if (this.$vuetify.theme.dark) {
          colour = plotly_dark.layout.colorway[colorway_index];
        }

        // First field with this axis_title: create or reuse axis
        let axis_info = axis_by_title[axis_title];
        if (!axis_info) {
          let yaxisKey = next_axis_slot <= 2 ? (next_axis_slot === 1 ? "y" : "y2") : "y2";
          axis_info = {
            yaxisKey,
            colours: [colour],
          };
          axis_by_title[axis_title] = axis_info;
          next_axis_slot++;

          let bullets = "<span style='color:" + colour + "'>•</span>";
          let titleText =
            bullets + " " + axis_title + (suffix ? " (" + suffix + ")" : "");

          if (yaxisKey === "y") {
            layout.yaxis = {
              title: titleText,
              fixedrange: true,
            };
            if (field_config.display == "percent") {
              layout.yaxis.tickformat = ",.0%";
              layout.yaxis.range = [0, 1.05];
            }
            if (field_config.display == "ms_to_minutes_seconds") {
              layout.yaxis.tickformat = "%M:%S";
            }
          } else {
            layout.yaxis2 = {
              title: titleText,
              overlaying: "y",
              side: "right",
              fixedrange: true,
              showgrid: false,
            };
            if (field_config.display == "percent") {
              layout.yaxis2.tickformat = ",.0%";
              layout.yaxis2.range = [0, 1.05];
            }
            if (field_config.display == "ms_to_minutes_seconds") {
              layout.yaxis2.tickformat = "%M:%S";
            }
            trace.yaxis = "y2";
          }
        } else {
          axis_info.colours.push(colour);
          let bullets = axis_info.colours
            .map((c) => "<span style='color:" + c + "'>•</span>")
            .join(" ");
          let axis_layout =
            axis_info.yaxisKey === "y" ? layout.yaxis : layout.yaxis2;
          if (axis_layout) {
            axis_layout.title =
              bullets +
              " " +
              axis_title +
              (suffix ? " (" + suffix + ")" : "");
          }
          if (axis_info.yaxisKey === "y2") {
            trace.yaxis = "y2";
          }
        }

        var current_level = 0;

        // Calculate tick ratio;
        let tick_ratio = Math.round(this.reports.length / this.max_ticks);
        if (!tick_ratio) {
          tick_ratio = 1;
        }

        for (var i in this.reports) {
          let report = this.reports[i];
          let score = this.reports[i].score;
          let value = score[field];

          if (typeof value == "undefined" || value === null) {
            value = null;
          } else {
            if (field_config.display == "ms_to_seconds") {
              value = Math.round(value / 1000);
            } else if (field_config.display == "ms_to_decimal_1_seconds") {
              value = Math.round(value / 100) / 10;
            } else if (field_config.display == "ms_to_decimal_2_seconds") {
              value = Math.round(value / 10) / 100;
            } else if (field_config.display == "ms_to_minutes_seconds") {
              value = ms2dt(value);
            } else if (field_config.display == "integer" || field_config.display == "fraction" || field_config.display == "fraction_with_percent") {
              value = Math.round(value);
            }
            else if (
              field_config.display == "decimal_2" ||
              field_config.display == "vergence_bi" ||
              field_config.display == "vergence_bo" ||
              field_config.display == "vergence_bu" ||
              field_config.display == "vergence_bd"
            ) {
              value = Math.round(value * 100) / 100;
            }
          }

          trace.x.push(i);
          trace.y.push(value);

          // X-Axis
          let date = new Date(report.start_time);
          var options = { month: "short", day: "numeric" };
          let formatter = new Intl.DateTimeFormat(this.$i18n.lang(), options);
          let ticktext = formatter.format(date);
          if (report.feedback) {
            ticktext += " 💬";
          }
          if (report.level != current_level) {
            ticktext += " △";
            current_level = report.level;
          }

          if (
            i % tick_ratio == 0 ||
            report.feedback ||
            report.level != current_level
          ) {
            layout.xaxis.tickvals.push(i);
            layout.xaxis.ticktext.push(ticktext);
          }
        }
        data.push(trace);

        colorway_index++;
      }

      return { data, layout };
    },

    data_layout_video() {
      let data = [];
      var layout = {
        showlegend: true,
        legend: {
          orientation: "h",
          x: 0.5,
          xanchor: "center",
          y: -0.2,
          yanchor: "top",
        },
        xaxis: {
          tickvals: [],
          ticktext: [],
        },
      };

      var trace = {
        x: [],
        y: [],
        text: [],
        type: "scatter",
        name: "",
        connectgaps: false,
      };

      // Hover template
      trace.hovertemplate = "%{text}";

      layout.yaxis = {
        title: this.$t("regimen_report_card.time_taken_seconds"),
        fixedrange: true,
      };

      // Calculate tick ratio;
      let tick_ratio = Math.round(this.reports.length / this.max_ticks);
      if (!tick_ratio) {
        tick_ratio = 1;
      }

      for (var i in this.reports) {
        let report = this.reports[i];
        let score = this.reports[i].score;

        let time_taken_ms = score["time_taken_ms"];
        let value = null;
        if (time_taken_ms !== null && typeof time_taken_ms !== "undefined") {
          value = Math.round(time_taken_ms / 1000);
        }

        // TODO: Change to minutes

        trace.x.push(i);
        trace.y.push(value);

        let text = "";
        if (time_taken_ms !== null && typeof time_taken_ms !== "undefined") {
          let mins = Math.floor(time_taken_ms / 1000 / 60);
          let secs = value % 60;
          text = mins + ":" + this.zeroPad(secs, 2);
        }
        trace.text.push(text);

        // X-Axis
        let date = new Date(report.start_time);
        var options = { month: "short", day: "numeric" };
        let formatter = new Intl.DateTimeFormat(this.$i18n.lang(), options);
        let ticktext = formatter.format(date);
        if (report.feedback) {
          ticktext += " 💬";
        }
        if (i % tick_ratio == 0 || report.feedback) {
          layout.xaxis.tickvals.push(i);
          layout.xaxis.ticktext.push(ticktext);
        }
      }
      data.push(trace);

      return { data, layout };
    },

    load_plotly() {
      // Lazy load a big-ass JS library for plotting
      if (typeof window.plotly_script === "undefined") {
        window.plotly_script = document.createElement("script");
        window.plotly_script.src = "https://cdn.plot.ly/plotly-1.58.5.min.js";
        window.plotly_script.type = "text/javascript";
        window.plotly_script.crossorigin = "anonymous";
        document
          .getElementsByTagName("head")[0]
          .appendChild(window.plotly_script);
      }
    },
    wait_for_plotly() {
      let self = this;
      var waiter;
      waiter = setInterval(function () {
        if (typeof window.Plotly !== "undefined") {
          clearInterval(waiter);
          self.loading = false;
          self.plot();
        }
      }, 100);
    },
    zeroPad(num, places) {
      return String(num).padStart(places, "0");
    },
  },
};

function ms2dt(v) {
  v = v / 1000;

  var MIN = 60;
  var HOUR = 60 * 60;

  var h = Math.floor(v / HOUR);
  var m = Math.floor((v - h * HOUR) / MIN);
  var s = Math.floor(v - h * HOUR - m * MIN);

  // you have to provide YYYY-MM-DD
  // for plotly to understand it as a date
  return `2017-01-01 ${h}:${pad(m)}:${pad(s)}`;
}

function pad(v) {
  return v < 10 ? "0" + v : String(v);
}
</script>

<style>
.modebar .modebar-btn.plotlyjsicon {
  display: none !important;
}

.modebar .modebar-group a[data-title="Box Select"] {
  display: none !important;
}

.modebar {
  left: 0%;
  padding-left: 10px !important;
  padding-top: 10px !important;
}

.modebar .modebar-group {
  margin-left: 8px !important;
  margin-top: 8px !important;
  scale: 1.5;
  z-index: 10;
}

.hover_report_table,
.hover_report_options,
.hover_report_events {
  border-collapse: collapse;
}

.hover_report_table tr,
.hover_report_options tr {
  border-bottom: 1px solid #ddd;
}

.hover_report_table .hover_report_label,
.hover_report_options .hover_report_label {
  font-weight: bold;
  width: 200px;
}

.hover_report_table .hover_report_label.hover_report_label_empty_value,
.hover_report_options .hover_report_label.hover_report_label_empty_value {
  font-weight: bold;
  width: 400px;
}

.hover_report_table .hover_report_label.hover_report_label_overview {
  width: 130px;
}

table.hover_report_table .hover_report_value {
  width: 200px;
}

.hover_report_table .hover_report_value.hover_report_value_overview {
  width: 270px;
}

.hover_report_events {
  width: 550px;
  margin-top: 30px;
}

.hover_report_events td {
  padding: 10px;
  border: 1px solid #ddd;
}

.hover_report_events .event_values:last-child .event_values_comma {
  display: none;
}

.regimen_report_card_plotly .main-svg g.axistext {
  display: none;
}

.regimen_report_card_graph_field_selector {
  position: fixed;
  z-index: 10;
  margin-left: 160px !important;
  margin-top: 10px !important;
}

.regimen_report_card_graph_field_selector .v-select__slot {
  max-width: 400px;
}

.hover_report_table_inner_wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  width: 100%;
  padding: 10px;
  overflow: hidden;
}

.phone-screen .hover_report_table .hover_report_label,
.phone-screen .hover_report_options .hover_report_label {
  font-weight: bold;
  width: 150px;
}

.phone-screen .hover_report_table .hover_report_label.hover_report_label_empty_value,
.phone-screen .hover_report_options .hover_report_label.hover_report_label_empty_value {
  font-weight: bold;
  width: 300px;
}

.phone-screen .hover_report_table .hover_report_label.hover_report_label_overview {
  width: 95px;
}

.phone-screen table.hover_report_table .hover_report_value {
  width: 150px;
}

.phone-screen .hover_report_table .hover_report_value.hover_report_value_overview {
  width: 205px;
}

.phone-screen .regimen_report_card_buttons {
  width: 100%;
  margin-top: 10px;
}

</style>
